"use client";

import useResponsive from "@/app/hooks/useResponsive";
import DisplaySize from "@/app/utils/DisplaySize";
import { useAuth } from "@/context/AuthContext";
import { useLanguageContext } from "@/context/LanguageContext";
import { ArrowUpRight } from "@/svg";
import KlardaComLogo from "@/svg/KlardaComLogo";
import { StarFilled } from "@ant-design/icons";
import { Button, Divider } from "antd";
import { debounce } from "lodash";
import { useTranslations } from "next-intl";
import { usePathname, useRouter } from "next/navigation";
import type { FC } from "react";
import { useCallback, useMemo, useState } from "react";
import DonateModal from "../DonateModal";
import DonateIcon from "../icons/DonateIcon";
import SearchModal from "../SearchModal";
import GuestActions from "./GuestAction";
import HeaderDropdownMenu from "./HeaderDropdownMenu";
import { buildMenuStructure } from "./MenuConfig";
import { MenuToggle } from "./MenuToggle";
import MobileMenu from "./MobileMenu";
import SearchInput from "./SearchInput";
import TokenCarousel from "./TokenCarousel";
import UserLoggedInDropdown from "./UserAction";
import FeedCarousel from "./FeedCarousel";

const Header: FC = () => {
  const t = useTranslations("home.header");
  const router = useRouter();
  const pathname = usePathname();
  const { authState } = useAuth();
  const { language } = useLanguageContext();
  const screenSize = useResponsive();
  const isMobile = screenSize < DisplaySize.Tablet;

  // State
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [openSearchModal, setOpenSearchModal] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [isDonateModalOpen, setIsDonateModalOpen] = useState(false);

  // Extract current path from pathname
  const currentPath = useMemo(() => {
    const pathParts = pathname.split("/");
    return "/" + (pathParts.length > 2 ? pathParts.slice(2).join("/") : "");
  }, [pathname]);

  // Handle menu item click
  const handleMenuClick = useCallback(
    (path: string) => {
      const isExternalUrl = /^https?:\/\//i.test(path);

      if (isExternalUrl) {
        window.open(path);
      } else {
        setIsMenuOpen(false);
        router.push(`/${language}${path}`);
      }
    },
    [language, router],
  );

  // Toggle menu open/close
  const toggleMenu = useCallback(() => setIsMenuOpen(prev => !prev), []);

  // Handle search text change with debounce
  const handleSearchTextChange = useMemo(() => debounce((text: string) => setSearchText(text), 500), []);

  // Build the menu structure
  const menuStructure = useMemo(() => buildMenuStructure(t), [t]);

  return (
    <>
      <SearchModal open={openSearchModal} onClose={() => setOpenSearchModal(false)} searchText={searchText} />

      <div className="bg-white w-full sticky top-0 z-50 transition-all duration-300 pb-1 shadow-sm">
        <div className="bg-gray-100 w-full">
          <TokenCarousel />
        </div>

        {/* Header Content */}
        <header className="h-full w-full max-w-[1440px] mx-auto">
          <div className="flex items-center justify-between px-4 py-4 lg:px-6">
            {/* Left Side - Logo and Menu */}
            <div className="flex items-center gap-8">
              {(isMobile ? !openSearchModal : true) && (
                <div onClick={() => handleMenuClick("/")} className="cursor-pointer w-fit">
                  <KlardaComLogo />
                </div>
              )}

              {!openSearchModal && (
                <div className="hidden lg:flex items-center space-x-6">
                  {menuStructure.map(item => (
                    <HeaderDropdownMenu key={item.key} menuItem={item} currentPath={currentPath} />
                  ))}
                </div>
              )}
            </div>

            {/* Right Side - Search and User */}
            <div className="flex items-center w-full justify-end">
              <SearchInput
                openSearchModal={openSearchModal}
                setOpenSearchModal={setOpenSearchModal}
                handleSearchTextChange={handleSearchTextChange}
              />

              <Divider type="vertical" className="h-8" />

              {!openSearchModal && (
                <>
                  <Button href={`/${language}/watchlists`} className="flex items-center" size="large">
                    Watchlists <StarFilled className="text-gray-500" />
                  </Button>
                  <Divider type="vertical" className="h-8" />

                  {/* User Actions - Logged in or Guest */}
                  <div className="hidden lg:flex items-center">
                    {authState === "logged" ? <UserLoggedInDropdown /> : <GuestActions />}
                  </div>

                  {/* Mobile Menu Toggle */}
                  <MenuToggle toggle={toggleMenu} isOpen={isMenuOpen} />
                </>
              )}
            </div>
          </div>

          {!openSearchModal && (
            <>
              <MobileMenu
                isOpen={isMenuOpen}
                menuStructure={menuStructure}
                currentPath={currentPath}
                handleMenuClick={handleMenuClick}
                setIsDonateModalOpen={setIsDonateModalOpen}
              />

              {/* Vietnam Button and Donate Button */}
              <div className="flex justify-between items-center gap-4 px-4 py-2 lg:px-6">
                <FeedCarousel />
                <div className="flex items-center gap-2">
                  <Button
                    icon={<DonateIcon />}
                    className="border-[#046C4E] text-[#046C4E] bg-[#DEF7EC] hover:bg-[#046C4E] hover:text-white hover:border-[#046C4E] transition-colors"
                    onClick={() => setIsDonateModalOpen(true)}
                  >
                    Donate
                  </Button>
                  <Button
                    type="primary"
                    className="flex items-center gap-2 font-semibold text-base"
                    href="https://vi.klarda.com/"
                    target="_blank"
                  >
                    {t("klarda_vn")}
                    <ArrowUpRight fill="white" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </header>
      </div>

      <DonateModal
        open={isDonateModalOpen}
        onClose={() => setIsDonateModalOpen(false)}
        data={
          {
            id: "",
            title: "",
            thumbnail: {
              alt: "",
              description: "",
              imageKey: "",
              quote: "",
            } as any,
            content: "",
            url: "",
            user_id: {
              email: "",
              id: "",
              web_wallet: {
                evm: process.env.NEXT_PUBLIC_DONATE_EVM_ADDRESS,
                solana: process.env.NEXT_PUBLIC_DONATE_SOLANA_ADDRESS,
              },
              profile_information: {
                avatar: "",
                bio: "",
                display_name: "",
              },
            },
          } as any
        }
      />
    </>
  );
};

export default Header;
